/**
 * The free-text step behind the report menu's "Other".
 *
 * The other report reasons are self-describing — Spam and Inappropriate say what
 * they mean and go straight through. "Other" said nothing at all: it filed a
 * report whose reason was the literal word "Other", so the admin email arrived
 * reading `Report Type: Other / Report Reason: Other` and nobody could act on it.
 * This asks the one question that makes it useful, and the answer rides through
 * as the report's `reason` — which the admin email already renders.
 *
 * Built as the same confirm popup used everywhere else (`.confirmPopup` with
 * `form-input bordered` on the box, like the order dispute dialog) rather than
 * as its own dialog, so it inherits the popup chrome and both themes.
 */
import React, { useState } from "react";
import shared from "../data/js/shared";

const {
  popupHeading,
  popupExplain,
  placeholder,
  submitLabel,
  cancelLabel,
} = shared.reportOther;

/**
 * Matches `ReportListingRequestSchema.reason` (z.string().max(1000)). Capped in
 * the box rather than only on the server: a report rejected at 1001 characters
 * would lose everything the person had typed.
 */
const MAX_MESSAGE = 1000;

interface ReportListingPopupProps {
  listingTitle?: string;
  onSubmit: (message: string) => void;
  onCancel: () => void;
}

const ReportListingPopup: React.FC<ReportListingPopupProps> = ({
  listingTitle,
  onSubmit,
  onCancel,
}) => {
  const [message, setMessage] = useState("");
  const trimmed = message.trim();

  return (
    <div className="confirmPopup report-listing-popup">
      <h3 className="heading">{popupHeading}</h3>
      <p className="explain">{popupExplain}</p>
      {listingTitle && (
        <p className="report-listing-popup__listing" data-testid="report-listing-title">
          {listingTitle}
        </p>
      )}
      <textarea
        className="report-listing-popup__message form-input bordered"
        data-testid="report-other-message"
        placeholder={placeholder}
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        rows={4}
        maxLength={MAX_MESSAGE}
        autoFocus
      />
      <button
        className="greenButton"
        data-testid="report-other-submit"
        // Disabled rather than validated on click: an empty "Other" report is
        // exactly the useless thing this step exists to stop.
        disabled={trimmed.length === 0}
        onClick={() => onSubmit(trimmed)}
      >
        {submitLabel}
      </button>
      <button
        className="hollowButton"
        data-testid="report-other-cancel"
        onClick={onCancel}
      >
        {cancelLabel}
      </button>
    </div>
  );
};

export default ReportListingPopup;
